import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, BookOpen, Check, ChevronRight } from 'lucide-react';
import StepHeading, { SectionCard, InfoNotice } from '../StepHeading';
import StepFooter from '../StepFooter';
import ApplicationSummary from '../ApplicationSummary';
import DisclosureModal from '../DisclosureModal';
import SignaturePad from '../../ui/SignaturePad';
import { TextInput } from '../../ui/form';
import { STEP_LABELS } from '../stepConfig';
import { typedNameMatches } from '../nameMatch';

const SECTION_STEPS = ['personal', 'license', 'record', 'employment'];

const scrollToElement = (el, { focus = false, block = 'center' } = {}) => {
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block });
  if (focus && typeof el.focus === 'function') el.focus({ preventScroll: true });
};

/**
 * Final step. Submit stays tappable: when something is missing, tapping it
 * opens a checklist of exactly what is left (each item jumps to the spot) and
 * highlights those spots, instead of a disabled button with no explanation.
 */
const ReviewSignStep = ({ application, legal, onEdit, onBack, onSubmit }) => {
  const [accepted, setAccepted] = useState({});
  const [openId, setOpenId] = useState(null);
  const [typedName, setTypedName] = useState('');
  const [signature, setSignature] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [serverErrors, setServerErrors] = useState([]);
  const [serverMessage, setServerMessage] = useState('');
  // Bumped on each failed submit; the effect scrolls once the error box exists.
  const [serverErrorTick, setServerErrorTick] = useState(0);

  const checklistRef = useRef(null);
  const serverErrorRef = useRef(null);
  const nameRef = useRef(null);
  const signatureRef = useRef(null);

  // The applicant is at the bottom of the page when Submit fails; bring the reason to them.
  useEffect(() => {
    if (serverErrorTick) scrollToElement(serverErrorRef.current, { focus: true });
  }, [serverErrorTick]);

  const personal = application.sections?.personal || {};
  const expectedName = `${personal.firstName || ''} ${personal.lastName || ''}`.trim();
  const disclosures = useMemo(() => legal?.disclosures || [], [legal?.disclosures]);
  const pendingDisclosures = disclosures.filter((d) => !accepted[d.id]);
  const nameOk = typedNameMatches(typedName, personal.firstName, personal.lastName);
  const incompleteSteps = SECTION_STEPS.filter((id) => !(application.progress?.completedSteps || []).includes(id));
  const openIndex = disclosures.findIndex((d) => d.id === openId);
  const openDisclosure = openIndex >= 0 ? disclosures[openIndex] : null;

  const nameServerError = serverErrors.find((e) => e.field === 'typedName')?.message;
  const nameError = (() => {
    if (nameServerError && !nameOk) return nameServerError;
    if (!typedName.trim()) return attempted ? `Type your full legal name: ${expectedName}` : undefined;
    if (!nameOk) return `Your name must include your first and last name as entered: ${expectedName}`;
    return undefined;
  })();
  const signatureError = attempted && !signature ? 'Draw your signature in the box above (finger, pen or mouse).' : undefined;

  const openNextPending = (afterId) => {
    const start = disclosures.findIndex((d) => d.id === afterId);
    const ordered = [...disclosures.slice(start + 1), ...disclosures.slice(0, Math.max(start, 0))];
    const next = ordered.find((d) => !accepted[d.id] && d.id !== afterId);
    setOpenId(next ? next.id : null);
  };

  // Everything still standing between the applicant and Submit, in page order.
  const missing = [
    ...incompleteSteps.map((id) => ({
      key: `step-${id}`,
      label: `Finish the ${STEP_LABELS[id]} step`,
      action: () => onEdit(id)
    })),
    // One item for all pending disclosures: the viewer moves on to the next by itself.
    ...(pendingDisclosures.length > 0 ? [{
      key: 'disclosures',
      label: pendingDisclosures.length === 1
        ? `Read and agree: ${pendingDisclosures[0].shortTitle || pendingDisclosures[0].title}`
        : `Read and agree to the ${pendingDisclosures.length} remaining disclosures`,
      action: () => setOpenId(pendingDisclosures[0].id)
    }] : []),
    ...(!nameOk ? [{
      key: 'name',
      label: typedName.trim() ? `Fix your typed name (must include ${expectedName})` : 'Type your full legal name',
      action: () => scrollToElement(nameRef.current?.querySelector('input'), { focus: true })
    }] : []),
    ...(!signature ? [{
      key: 'signature',
      label: 'Draw your signature',
      action: () => scrollToElement(signatureRef.current)
    }] : [])
  ];

  const requirements = [
    { key: 'steps', label: 'All application steps finished', done: incompleteSteps.length === 0 },
    { key: 'disclosures', label: `Disclosures agreed (${disclosures.length - pendingDisclosures.length} of ${disclosures.length})`, done: pendingDisclosures.length === 0 },
    { key: 'name', label: 'Full legal name typed', done: nameOk },
    { key: 'signature', label: 'Signature drawn', done: Boolean(signature) }
  ];

  const showChecklist = () => {
    setAttempted(true);
    // Let the checklist render before scrolling to it.
    setTimeout(() => scrollToElement(checklistRef.current, { focus: true, block: 'start' }), 0);
  };

  const handleSubmit = async () => {
    if (missing.length > 0) {
      showChecklist();
      return;
    }
    setServerErrors([]);
    setServerMessage('');
    setSubmitting(true);
    try {
      const result = await onSubmit({ disclosures: accepted, typedName: typedName.trim(), signatureDataUrl: signature });
      if (!result?.ok) {
        setServerErrors(Array.isArray(result?.errors) ? result.errors : []);
        setServerMessage(result?.message || 'We could not submit your application.');
        setServerErrorTick((n) => n + 1);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const helperText = missing.length > 0
    ? `${missing.length} ${missing.length === 1 ? 'thing' : 'things'} left before you can submit. Show me`
    : 'Everything is ready. You can submit.';

  return (
    <div>
      <StepHeading title="Review & Sign" description="Check everything one last time, read the disclosures, then sign and submit." />

      {incompleteSteps.length > 0 && (
        <InfoNotice tone="warning" title="Some steps are not finished">
          <ul className="mt-1 list-disc pl-5">
            {incompleteSteps.map((id) => (
              <li key={id}><button type="button" onClick={() => onEdit(id)} className="font-medium underline">{STEP_LABELS[id]}</button></li>
            ))}
          </ul>
        </InfoNotice>
      )}

      {(serverErrors.length > 0 || serverMessage) && (
        <div ref={serverErrorRef} tabIndex={-1} role="alert" className="mt-4 scroll-mt-40 rounded-lg border border-red-200 bg-red-50 p-4 focus:outline-none focus:ring-2 focus:ring-red-400">
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-red-800">{serverMessage}</p>
              {serverErrors.length > 0 && (
                <ul className="mt-2 space-y-1 text-sm text-red-700">
                  {serverErrors.map((err, index) => (
                    <li key={`${err.field}-${index}`}>
                      {err.step && err.step !== 'review' ? (
                        <button type="button" onClick={() => onEdit(err.step)} className="underline underline-offset-2">
                          <span className="font-medium">{STEP_LABELS[err.step]}:</span> {err.message}
                        </button>
                      ) : err.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mt-5 space-y-5">
        <ApplicationSummary application={application} onEdit={onEdit} />

        <div className="scroll-mt-40">
          <SectionCard
            title="Disclosures"
            description="Open each one, read it to the end, and agree. These authorize the checks the company must run before you can drive."
          >
            <ul className="divide-y divide-gray-100">
              {disclosures.map((d) => {
                const isAccepted = Boolean(accepted[d.id]);
                const flag = attempted && !isAccepted;
                return (
                  <li key={d.id} className={`flex items-center justify-between gap-3 py-3 ${flag ? '-mx-2 rounded-md bg-red-50 px-2' : ''}`}>
                    <div className="flex min-w-0 items-center gap-3">
                      <span
                        className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border ${isAccepted ? 'border-green-600 bg-green-600 text-white' : flag ? 'border-red-400 bg-white text-transparent' : 'border-gray-300 bg-white text-transparent'}`}
                        aria-hidden="true"
                      >
                        <Check className="h-3.5 w-3.5" strokeWidth={3} />
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900">{d.shortTitle || d.title}</p>
                        <p className={`text-xs ${isAccepted ? 'text-green-700' : flag ? 'font-medium text-red-700' : 'text-gray-500'}`}>
                          {isAccepted ? 'Agreed' : flag ? 'Required: read and agree' : 'Not yet agreed'}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setOpenId(d.id)}
                      className={`inline-flex flex-shrink-0 items-center gap-1 rounded-md border px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 ${isAccepted ? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50' : 'border-blue-600 bg-blue-600 text-white hover:bg-blue-700'}`}
                    >
                      <BookOpen className="h-4 w-4" aria-hidden="true" /> {isAccepted ? 'Read again' : 'Read & agree'}
                    </button>
                  </li>
                );
              })}
            </ul>
          </SectionCard>
        </div>

        <SectionCard title="Certification and signature">
          <p className="text-sm leading-6 text-gray-800">{legal?.certification?.text}</p>
          <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
            <div ref={nameRef} className="scroll-mt-40">
              <TextInput
                label="Type your full legal name"
                required
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                placeholder={expectedName}
                autoComplete="name"
                hint={`Type it as on your application, e.g. "${expectedName}"`}
                error={nameError}
              />
            </div>
            <div ref={signatureRef} className="scroll-mt-40 md:col-span-2">
              <p className="mb-1.5 block text-sm font-medium text-gray-700">Draw your signature <span className="text-red-600" aria-hidden="true">*</span></p>
              <SignaturePad value={signature} onChange={setSignature} height={190} error={signatureError} />
              <p className="mt-2 text-xs text-gray-500">
                By signing, you agree that your electronic signature is the legal equivalent of your handwritten signature. We record the date, time, and device used to sign.
              </p>
            </div>
          </div>
        </SectionCard>

        <div
          ref={checklistRef}
          tabIndex={-1}
          role={attempted && missing.length > 0 ? 'alert' : undefined}
          className={`scroll-mt-40 rounded-xl border p-4 focus:outline-none focus:ring-2 focus:ring-blue-400 sm:p-5 ${attempted && missing.length > 0 ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white shadow-sm'}`}
        >
          <h3 className={`text-base font-semibold ${attempted && missing.length > 0 ? 'text-red-800' : 'text-gray-900'}`}>
            {missing.length === 0 ? 'Ready to submit' : attempted ? 'Before you can submit' : 'To submit, you need'}
          </h3>
          <ul className="mt-3 space-y-2">
            {requirements.map((req) => (
              <li key={req.key} className="flex items-center gap-2 text-sm">
                <span
                  className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full ${req.done ? 'bg-green-600 text-white' : attempted ? 'border-2 border-red-400' : 'border-2 border-gray-300'}`}
                  aria-hidden="true"
                >
                  {req.done && <Check className="h-3 w-3" strokeWidth={3} />}
                </span>
                <span className={req.done ? 'text-gray-700' : attempted ? 'font-medium text-red-800' : 'text-gray-800'}>
                  {req.label}
                  <span className="sr-only">{req.done ? ' (done)' : ' (not done)'}</span>
                </span>
              </li>
            ))}
          </ul>
          {attempted && missing.length > 0 && (
            <div className="mt-4 border-t border-red-200 pt-3">
              <p className="text-sm text-red-800">Tap an item to go to it:</p>
              <ul className="mt-2 space-y-1">
                {missing.map((item) => (
                  <li key={item.key}>
                    <button
                      type="button"
                      onClick={item.action}
                      className="flex w-full items-center justify-between gap-2 rounded-md bg-white px-3 py-2.5 text-left text-sm font-medium text-red-800 ring-1 ring-red-200 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-400"
                    >
                      <span>{item.label}</span>
                      <ChevronRight className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      <DisclosureModal
        disclosure={openDisclosure}
        open={Boolean(openDisclosure)}
        onClose={() => setOpenId(null)}
        onAccept={(id) => {
          setAccepted((prev) => ({ ...prev, [id]: true }));
          openNextPending(id);
        }}
        closeOnAccept={false}
        accepted={openId ? Boolean(accepted[openId]) : false}
        position={openDisclosure ? { index: openIndex + 1, total: disclosures.length } : null}
        hasNext={Boolean(openDisclosure) && pendingDisclosures.some((d) => d.id !== openId)}
      />

      <StepFooter
        onBack={onBack}
        onNext={handleSubmit}
        nextLabel={submitting ? 'Submitting and generating your PDF…' : 'Submit application'}
        nextVariant="success"
        isSubmitting={submitting}
        helperText={submitting ? undefined : helperText}
        helperTone={missing.length > 0 && attempted ? 'error' : 'muted'}
        onHelperClick={missing.length > 0 ? showChecklist : undefined}
      />
    </div>
  );
};

export default ReviewSignStep;
